/**
 * AgentsPage.tsx
 *
 * Agents manager: lists the tenant's agents (GET /v1/agents) with an Edit
 * action per row and a "New agent" button. Create/edit are handled inline by
 * AgentCreatePage (create mode, or edit mode with an agentId), returning to the
 * list on completion.
 */

import { useCallback, useEffect, useState } from 'react'
import { Pencil, Plus } from 'lucide-react'

import { api } from '../api/client'
import { GlassPanel } from './common/GlassPanel'
import { AgentCreatePage } from './AgentCreatePage'
import { cronLabel } from '../lib/scheduleCron'

interface AgentRow {
  id: string
  name: string
  status: string
  llmProvider?: string
  llmModel?: string
  updatedAt?: string
}

interface ScheduleRow {
  id: string
  agentId: string
  scheduleExpr: string
  status: string
}

type LoadState = 'loading' | 'ready' | 'error'
type View =
  | { mode: 'list' }
  | { mode: 'create' }
  | { mode: 'edit'; agentId: string }

const STATUS_COLOR: Record<string, string> = {
  draft:    '#94a3b8',
  active:   '#22c55e',
  paused:   '#f59e0b',
  archived: '#64748b',
}

function StatusPill({ status }: { status: string }) {
  const color = STATUS_COLOR[status] ?? '#64748b'
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[9.5px] font-[800] tracking-[0.1em] uppercase border"
      style={{ color, background: `${color}16`, borderColor: `${color}30` }}
    >
      {status}
    </span>
  )
}

export function AgentsPage() {
  const [view, setView] = useState<View>({ mode: 'list' })
  const [agents, setAgents] = useState<AgentRow[]>([])
  const [scheduleByAgent, setScheduleByAgent] = useState<Record<string, ScheduleRow>>({})
  const [loadState, setLoadState] = useState<LoadState>('loading')

  const load = useCallback(async () => {
    setLoadState('loading')
    try {
      const [agentsRes, schedRes] = await Promise.allSettled([
        api.get<{ items: AgentRow[] | null }>('/v1/agents'),
        api.get<{ items: ScheduleRow[] | null }>('/v1/schedules'),
      ])
      if (agentsRes.status === 'fulfilled') {
        setAgents(Array.isArray(agentsRes.value.items) ? agentsRes.value.items : [])
      }
      if (schedRes.status === 'fulfilled') {
        const map: Record<string, ScheduleRow> = {}
        for (const s of schedRes.value.items ?? []) {
          if (s.status === 'active' && !map[s.agentId]) map[s.agentId] = s
        }
        setScheduleByAgent(map)
      }
      setLoadState(agentsRes.status === 'fulfilled' ? 'ready' : 'error')
    } catch {
      setLoadState('error')
    }
  }, [])

  useEffect(() => {
    if (view.mode === 'list') void load()
  }, [view, load])

  const backToList = useCallback(() => setView({ mode: 'list' }), [])

  if (view.mode === 'create') {
    return <AgentCreatePage onDone={backToList} />
  }
  if (view.mode === 'edit') {
    return <AgentCreatePage agentId={view.agentId} onDone={backToList} />
  }

  // ── List view ────────────────────────────────────────────────────────────────
  return (
    <div className="pt-2 max-w-[820px]">
      <div className="flex items-start justify-between gap-6 mb-7">
        <div>
          <h1 className="text-[22px] font-[900] text-[#dce9f7] m-0 mb-2 tracking-[-0.03em]">Agents</h1>
          <p className="text-[13px] text-[#4e6480] leading-[1.65] m-0 max-w-[520px]">
            Manage your agents — edit name, status, execution mode, and model.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setView({ mode: 'create' })}
          className="inline-flex items-center gap-[7px] px-[14px] py-[9px] rounded-[9px] border border-[rgba(56,189,248,0.4)] bg-[rgba(56,189,248,0.12)] text-[#38bdf8] text-[12px] font-bold cursor-pointer transition-colors hover:bg-[rgba(56,189,248,0.2)] flex-shrink-0"
        >
          <Plus size={14} /> New agent
        </button>
      </div>

      {loadState === 'loading' && (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[58px] rounded-[12px] border border-[rgba(148,163,184,0.08)] bg-[rgba(11,18,32,0.5)] animate-pulse" />
          ))}
        </div>
      )}

      {loadState === 'error' && (
        <div className="text-[13px] text-[#f87171] px-4 py-3 bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-xl">
          Failed to load agents.{' '}
          <button className="underline bg-transparent border-none text-[#f87171] cursor-pointer p-0" onClick={() => void load()}>Retry</button>
        </div>
      )}

      {loadState === 'ready' && agents.length === 0 && (
        <div className="text-[13px] text-[#4e6480] leading-[1.65] px-[18px] py-[16px] bg-[rgba(148,163,184,0.05)] border border-[rgba(148,163,184,0.1)] rounded-xl">
          No agents yet. Click <span className="text-[#38bdf8] font-semibold">New agent</span> to create one.
        </div>
      )}

      {loadState === 'ready' && agents.length > 0 && (
        <div className="flex flex-col gap-2">
          {agents.map((a) => (
            <GlassPanel key={a.id} className="px-4 py-[12px] flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-bold text-[#dce9f7] truncate">{a.name}</span>
                  <StatusPill status={a.status} />
                  {scheduleByAgent[a.id] && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-bold text-[#38bdf8] bg-[rgba(56,189,248,0.1)] border border-[rgba(56,189,248,0.25)] whitespace-nowrap"
                      title={`Schedule: ${scheduleByAgent[a.id].scheduleExpr}`}
                    >
                      ⏰ {cronLabel(scheduleByAgent[a.id].scheduleExpr)}
                    </span>
                  )}
                </div>
                <div className="text-[10.5px] text-[#4e6480] font-mono truncate mt-0.5">
                  {a.llmProvider && a.llmModel ? `${a.llmProvider} / ${a.llmModel}` : a.id}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setView({ mode: 'edit', agentId: a.id })}
                className="inline-flex items-center gap-[6px] px-[12px] py-[7px] rounded-[8px] border border-[rgba(148,163,184,0.2)] bg-[rgba(148,163,184,0.06)] text-[#c8d9ee] text-[11px] font-bold cursor-pointer transition-colors hover:bg-[rgba(148,163,184,0.12)] flex-shrink-0"
              >
                <Pencil size={12} /> Edit
              </button>
            </GlassPanel>
          ))}
        </div>
      )}
    </div>
  )
}
